import React, { useState, useEffect, useRef } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

import WorkOrderDetailModal from './components/WorkOrderDetailModal';
import WorkOrderImportModal from './components/WorkOrderImportModal';

const QA_STATUSES = [
  'Pending Review',
  'In QA Inspection',
  'QA Approved',
  'Ready for Invoicing',
  'Invoiced',
  'Rejected',
];

const STATUS_COLORS = {
  'Pending Review': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'In QA Inspection': 'bg-blue-100 text-blue-800 border-blue-200',
  'QA Approved': 'bg-green-100 text-green-800 border-green-200',
  'Ready for Invoicing': 'bg-purple-100 text-purple-800 border-purple-200',
  'Invoiced': 'bg-teal-100 text-teal-800 border-teal-200',
  'Rejected': 'bg-red-100 text-red-800 border-red-200',
};

const WorkOrderTracker = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(null);

  const fetchWorkOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase?.from('work_orders')?.select('*')?.order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setWorkOrders(data || []);
    } catch (err) {
      console.error('Error fetching work orders:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkOrders();
  }, []);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      setUpdatingStatus(orderId);
      const { error: updateError } = await supabase?.from('work_orders')?.update({ qa_status: newStatus })?.eq('id', orderId);

      if (updateError) throw updateError;
      setWorkOrders(prev =>
        prev?.map(o => o?.id === orderId ? { ...o, qa_status: newStatus } : o)
      );
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status: ' + err?.message);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleOpenDetail = (order) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
  };

  const handleDetailClose = () => {
    setShowDetailModal(false);
    setSelectedOrder(null);
    fetchWorkOrders();
  };

  const handleImportClose = () => {
    setShowImportModal(false);
    fetchWorkOrders();
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this work order?')) return;
    try {
      const { error: delError } = await supabase?.from('work_orders')?.delete()?.eq('id', orderId);
      if (delError) throw delError;
      setWorkOrders(prev => prev?.filter(o => o?.id !== orderId));
    } catch (err) {
      alert('Failed to delete: ' + err?.message);
    }
  };

  const filteredOrders = workOrders?.filter(order => {
    const matchesStatus = filterStatus === 'All' || order?.qa_status === filterStatus;
    const matchesSearch =
      !searchQuery ||
      order?.work_order_number?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      order?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      order?.location?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      order?.assigned_to?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const statusCounts = QA_STATUSES?.reduce((acc, s) => {
    acc[s] = workOrders?.filter(o => o?.qa_status === s)?.length;
    return acc;
  }, {});

  const breadcrumbItems = [
    { label: 'Home', path: '/compliance-overview' },
    { label: 'Work Order Tracker', path: '/work-order-tracker' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="px-6 py-6 max-w-screen-2xl mx-auto">
          <Breadcrumb items={breadcrumbItems} />

          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-4 mb-6">
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground">HxGN Work Order Tracker</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Import, manage, and track work orders with QA inspection statuses and PDF attachments
              </p>
            </div>
            <Button
              variant="primary"
              iconName="Upload"
              onClick={() => setShowImportModal(true)}
            >
              Import Work Orders
            </Button>
          </div>

          {/* Status Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            {QA_STATUSES?.map(status => (
              <button
                key={status}
                onClick={() => setFilterStatus(filterStatus === status ? 'All' : status)}
                className={`p-3 rounded-lg border text-left transition-all ${
                  filterStatus === status
                    ? STATUS_COLORS?.[status] + 'border-2' :'bg-card border-border hover:border-primary/40'
                }`}
              >
                <div className="text-2xl font-bold text-foreground">{statusCounts?.[status] || 0}</div>
                <div className="text-xs text-muted-foreground mt-1 leading-tight">{status}</div>
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by WO number, description, location, assigned to..."
                value={searchQuery}
                onChange={e => setSearchQuery(e?.target?.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e?.target?.value)}
              className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="All">All Statuses ({workOrders?.length})</option>
              {QA_STATUSES?.map(s => (
                <option key={s} value={s}>{s} ({statusCounts?.[s] || 0})</option>
              ))}
            </select>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
              <Icon name="AlertCircle" size={16} className="inline mr-2" />
              {error}
            </div>
          )}

          {/* Table */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm text-muted-foreground">Loading work orders...</span>
                </div>
              </div>
            ) : filteredOrders?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                  <Icon name="ClipboardList" size={28} className="text-muted-foreground" />
                </div>
                <div className="text-center">
                  <p className="font-medium text-foreground">No work orders found</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {workOrders?.length === 0
                      ? 'Import work orders via Excel to get started' :'Try adjusting your filters'}
                  </p>
                </div>
                {workOrders?.length === 0 && (
                  <Button variant="primary" iconName="Upload" onClick={() => setShowImportModal(true)}>
                    Import Work Orders
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/40">
                      <th className="text-left px-4 py-3 font-semibold text-muted-foreground">WO Number</th>
                      <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Description</th>
                      <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Location</th>
                      <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Assigned To</th>
                      <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Amount</th>
                      <th className="text-left px-4 py-3 font-semibold text-muted-foreground">QA Status</th>
                      <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Attachments</th>
                      <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders?.map((order, idx) => (
                      <tr
                        key={order?.id}
                        className={`border-b border-border hover:bg-muted/30 transition-colors ${idx % 2 === 0 ? '' : 'bg-muted/10'}`}
                      >
                        <td className="px-4 py-3 font-mono font-medium text-foreground">
                          {order?.work_order_number}
                        </td>
                        <td className="px-4 py-3 text-foreground max-w-[200px] truncate" title={order?.description}>
                          {order?.description || <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-4 py-3 text-foreground">
                          {order?.location || <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-4 py-3 text-foreground">
                          {order?.assigned_to || <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-4 py-3 text-foreground">
                          {order?.amount != null
                            ? `$${Number(order?.amount)?.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                            : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          {updatingStatus === order?.id ? (
                            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <select
                              value={order?.qa_status}
                              onChange={e => handleStatusChange(order?.id, e?.target?.value)}
                              className={`text-xs font-medium px-2 py-1 rounded-full border cursor-pointer focus:outline-none ${STATUS_COLORS?.[order?.qa_status] || 'bg-muted text-foreground border-border'}`}
                            >
                              {QA_STATUSES?.map(s => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span
                              title={order?.work_order_pdf_path ? 'Work Order PDF attached' : 'No Work Order PDF'}
                              className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${order?.work_order_pdf_path ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-muted text-muted-foreground border-border'}`}
                            >
                              <Icon name="FileText" size={12} />
                              WO
                            </span>
                            <span
                              title={order?.invoice_pdf_path ? 'Invoice PDF attached' : 'No Invoice PDF'}
                              className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${order?.invoice_pdf_path ? 'bg-green-50 text-green-700 border-green-200' : 'bg-muted text-muted-foreground border-border'}`}
                            >
                              <Icon name="Receipt" size={12} />
                              INV
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleOpenDetail(order)}
                              className="p-1.5 rounded-md hover:bg-primary/10 text-primary transition-colors"
                              title="View / Edit Details"
                            >
                              <Icon name="Eye" size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteOrder(order?.id)}
                              className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive transition-colors"
                              title="Delete"
                            >
                              <Icon name="Trash2" size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="mt-3 text-xs text-muted-foreground">
            Showing {filteredOrders?.length} of {workOrders?.length} work orders
          </div>
        </div>
      </main>
      {showDetailModal && selectedOrder && (
        <WorkOrderDetailModal
          order={selectedOrder}
          onClose={handleDetailClose}
          statusColors={STATUS_COLORS}
          qaStatuses={QA_STATUSES}
        />
      )}
      {showImportModal && (
        <WorkOrderImportModal
          onClose={handleImportClose}
        />
      )}
    </div>
  );
};

export default WorkOrderTracker;
